// @ts-nocheck
import { useEffect, type FC } from "react";
import { Helmet } from "react-helmet-async";
import PropTypes from "prop-types";
import { usePathname } from "src/hooks/use-pathname";

const BASE_URL = "https://www.opensource.clinic";
const DEFAULT_IMAGE = `${BASE_URL}/opensourceclinicicontransparant.png`;

interface SeoProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  type?: string;
  canonical?: string;
  noindex?: boolean;
  nofollow?: boolean;
  structuredData?: object;
  hreflang?: { lang: string; url: string }[];
  exactTitle?: boolean;
  htmlLang?: string;
}

export const Seo: FC<SeoProps> = (props) => {
  const {
    title,
    description,
    keywords,
    image = DEFAULT_IMAGE,
    type = "article",
    canonical,
    noindex = false,
    nofollow = false,
    structuredData,
    hreflang,
    exactTitle = false,
    htmlLang,
  } = props;

  const pathname = usePathname();
  const fullTitle = title
    ? exactTitle
      ? title
      : `${title} | Opensource.clinic`
    : "Opensource.clinic | Nightscout Hosting & Personalized Coaching for Diabetes Management";

  const defaultDescription =
    "OpenSource.Clinic offers Nightscout hosting with personalized coaching for your diabetes management. Join a global movement using transparent, customizable, and affordable solutions to live healthier.";

  const metaDescription = description || defaultDescription;
  const canonicalUrl = canonical || `${BASE_URL}${pathname}`;
  const ogImage = image.startsWith("http") ? image : `${BASE_URL}${image}`;

  // Build robots meta content
  const robotsContent = [];
  if (noindex) robotsContent.push("noindex");
  else robotsContent.push("index");
  if (nofollow) robotsContent.push("nofollow");
  else robotsContent.push("follow");

  useEffect(() => {
    const originals: { node: Element; content: string | null }[] = [];

    const sync = (selector: string, content: string) => {
      document.querySelectorAll(selector).forEach((node) => {
        if (node.getAttribute("content") === content) {
          return;
        }
        originals.push({ node, content: node.getAttribute("content") });
        node.setAttribute("content", content);
      });
    };

    sync('meta[name="description"]', metaDescription);
    if (keywords) {
      sync('meta[name="keywords"]', keywords);
    }

    return () => {
      originals.forEach(({ node, content }) => {
        if (content === null) {
          node.removeAttribute("content");
          return;
        }
        node.setAttribute("content", content);
      });
    };
  }, [metaDescription, keywords]);

  return (
    <Helmet htmlAttributes={htmlLang ? { lang: htmlLang } : undefined}>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={metaDescription} />
      {keywords && <meta name="keywords" content={keywords} />}
      <meta name="robots" content={robotsContent.join(", ")} />

      {/* Canonical URL */}
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph Tags */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="Opensource.clinic" />
      {htmlLang === "nl" && <meta property="og:locale" content="nl_NL" />}

      {/* Hreflang Tags */}
      {hreflang &&
        hreflang.map((item) => (
          <link
            key={item.lang}
            rel="alternate"
            hrefLang={item.lang}
            href={item.url}
          />
        ))}

      {/* Structured Data (JSON-LD) */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
};

Seo.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  keywords: PropTypes.string,
  image: PropTypes.string,
  type: PropTypes.string,
  canonical: PropTypes.string,
  noindex: PropTypes.bool,
  nofollow: PropTypes.bool,
  structuredData: PropTypes.object,
  hreflang: PropTypes.arrayOf(
    PropTypes.shape({
      lang: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired,
    })
  ),
  exactTitle: PropTypes.bool,
  htmlLang: PropTypes.string,
};
